/**
 * The lifecycle events, editable in place.
 *
 * Every edit commits per field and shows an inline pending state on its own row. It does not
 * predict the consequences of an edit: correcting a date regenerates both rule chains, and the
 * screen re-renders from what the server returns rather than patching locally.
 */

import { useState } from 'react';
import { Button } from '~/ui/Button';
import { Field, DateInput, TextInput, SegmentedToggle } from '~/ui/Field';
import { Pill } from '~/ui/Pill';
import { ValidationMessages } from '~/ui/Feedback';
import { ConfirmDialog } from '~/ui/Modal';
import {
  useAddLifecycleEvent,
  useUpdateLifecycleEvent,
  useDeleteLifecycleEvent,
} from '~/api/mutations';
import { useDebouncedValidation } from '~/hooks/useDebouncedValidation';
import { validateLifecycleDraft } from '~/api/validate';
import { useToast } from '~/hooks/useToast';
import { problemsOf } from '~/api/client';
import { fmtDate } from '~/lib/format';
import type { JurisdictionDetail, LifecycleEvent, MatterSummary } from '~/api/schema';
import styles from './Overview.module.css';

export interface LifecycleEditorProps {
  register: JurisdictionDetail;
  matterLabels: Readonly<Record<string, string>>;
}

export function LifecycleEditor({ register, matterLabels }: LifecycleEditorProps) {
  const update = useUpdateLifecycleEvent(register.code);
  const remove = useDeleteLifecycleEvent(register.code);
  const toast = useToast();
  const [confirming, setConfirming] = useState<LifecycleEvent | null>(null);
  const [adding, setAdding] = useState<'renamed' | 'closed' | null>(null);

  const commit = (event: LifecycleEvent, patch: Parameters<typeof update.mutate>[0]['body']) => {
    update.mutate(
      { id: event.id, body: patch, etag: event.etag },
      {
        onSuccess: (written) => {
          for (const warning of written.warnings ?? []) toast.show('ok', warning);
        },
        onError: (error) => {
          for (const problem of problemsOf(error)) toast.show('error', problem);
        },
      },
    );
  };

  const pendingId = update.isPending ? update.variables?.id : undefined;

  return (
    <>
      <div className={styles.eventScroll}>
        <table className={styles.eventTable}>
          <caption className="visually-hidden">
            Every dated event in this register&rsquo;s history
          </caption>
          <thead>
            <tr>
              <th scope="col">Event</th>
              <th scope="col">Date</th>
              <th scope="col">Detail</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {register.lifecycle.map((event) => (
              <tr key={event.id}>
                <td>
                  {event.kind === 'opened'
                    ? 'Register opened'
                    : event.kind === 'renamed'
                      ? 'Renamed'
                      : 'Register closed'}
                  {event.scheduled ? (
                    <>
                      {' '}
                      <Pill tone="quest" size="mini">
                        Scheduled
                      </Pill>
                    </>
                  ) : null}
                  {pendingId === event.id ? (
                    <div className={styles.pending} role="status">
                      Saving…
                    </div>
                  ) : null}
                </td>
                <td style={{ width: 196 }}>
                  <DateInput
                    value={event.date ?? ''}
                    min={register.earliestDate}
                    aria-label={`Date of the ${event.kind} event`}
                    onChange={(next) => next && commit(event, { date: next })}
                  />
                  {event.precision === 'year' ? (
                    <div className={styles.pending}>Known to the year only</div>
                  ) : null}
                </td>
                <td>
                  {event.kind === 'opened' ? (
                    <div className={styles.renameField}>
                      {/* Keyed on the saved value so a server correction replaces what is typed. */}
                      <TextInput
                        key={`${event.id}-name-${event.name ?? ''}`}
                        defaultValue={event.name ?? ''}
                        aria-label="Name at opening"
                        onBlur={(e) => {
                          if (e.currentTarget.value !== (event.name ?? '')) {
                            commit(event, { name: e.currentTarget.value });
                          }
                        }}
                      />
                      <TextInput
                        key={`${event.id}-registry-${event.registry ?? ''}`}
                        defaultValue={event.registry ?? ''}
                        aria-label="Registry at opening"
                        onBlur={(e) => {
                          if (e.currentTarget.value !== (event.registry ?? '')) {
                            commit(event, { registry: e.currentTarget.value });
                          }
                        }}
                      />
                    </div>
                  ) : event.kind === 'renamed' ? (
                    <div className={styles.renameField}>
                      <SegmentedToggle
                        aria-label="What this rename changed"
                        value={event.field ?? 'name'}
                        onChange={(next) => commit(event, { field: next })}
                        options={[
                          { value: 'name', label: 'Jurisdiction' },
                          { value: 'registry', label: 'Registry' },
                        ]}
                      />
                      <span className={styles.fromTo}>was {event.fromLabel} →</span>
                      <TextInput
                        key={`${event.id}-to-${event.to ?? ''}`}
                        defaultValue={event.to ?? ''}
                        aria-label="New value"
                        onBlur={(e) => {
                          if (e.currentTarget.value !== (event.to ?? '')) {
                            commit(event, { to: e.currentTarget.value });
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <span className={styles.successionMeta}>
                      Rights passed to{' '}
                      {register.successors.length > 0
                        ? register.successors
                            .map((successor) =>
                              successor.matter
                                ? `${successor.name} (${matterLabels[successor.matter] ?? successor.matter})`
                                : successor.name,
                            )
                            .join(', ')
                        : 'nobody recorded yet'}
                      .
                    </span>
                  )}
                </td>
                <td className={styles.rowActions}>
                  {event.kind === 'opened' ? (
                    <span className={styles.pending}>
                      A register with no start has no span
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirming(event)}
                      pending={remove.isPending && remove.variables?.id === event.id}
                    >
                      {event.kind === 'closed' ? 'Reopen' : 'Remove'}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className={styles.tableFoot}>
        No event may fall before {fmtDate(register.earliestDate)} — the earliest date this
        register&rsquo;s matters allow.
      </p>

      <div className={styles.addRow}>
        <Button onClick={() => setAdding('renamed')}>Add a rename</Button>
        {register.closedOn ? null : (
          <Button onClick={() => setAdding('closed')}>Mark the register closed</Button>
        )}
      </div>

      {adding ? (
        <AddLifecycleEventDialog
          register={register}
          kind={adding}
          onClose={() => setAdding(null)}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          title={confirming.kind === 'closed' ? 'Reopen this register?' : 'Remove this rename?'}
          body={
            confirming.kind === 'closed' ? (
              <p>
                The closure dated {fmtDate(confirming.date)} will be deleted and the register will
                read as active again. Both rule chains are laid out over the span the lifecycle
                defines, so removing it redraws every matter tab.
              </p>
            ) : (
              <p>
                The rename dated {fmtDate(confirming.date)} will be deleted. The register&rsquo;s
                current name and every name band are derived from this list, so removing it
                changes what the register is called from that date onwards.
              </p>
            )
          }
          confirmLabel={confirming.kind === 'closed' ? 'Reopen it' : 'Remove it'}
          cancelLabel="Keep it"
          destructive
          pending={remove.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            remove.mutate(
              { id: confirming.id, etag: confirming.etag },
              {
                onSuccess: () => {
                  toast.show('ok', 'The event was removed and the chains were redrawn.');
                  setConfirming(null);
                },
                onError: (error) => {
                  for (const problem of problemsOf(error)) toast.show('error', problem);
                },
              },
            );
          }}
        />
      ) : null}
    </>
  );
}

function AddLifecycleEventDialog({
  register,
  kind,
  onClose,
}: {
  register: JurisdictionDetail;
  kind: 'renamed' | 'closed';
  onClose: () => void;
}) {
  const add = useAddLifecycleEvent(register.code);
  const toast = useToast();

  const [date, setDate] = useState(register.today);
  const [field, setField] = useState<'name' | 'registry'>('name');
  const [to, setTo] = useState('');
  const [successors, setSuccessors] = useState('');
  const [citation, setCitation] = useState('');

  const body = {
    kind,
    date,
    precision: 'day' as const,
    ...(kind === 'renamed' ? { field, to } : {}),
    ...(kind === 'closed'
      ? {
          successors: successors
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean),
        }
      : {}),
    ...(citation ? { citation } : {}),
  };

  const check = useDebouncedValidation({
    body,
    enabled: kind === 'closed' || to.trim().length > 0,
    validate: (draft, signal) => validateLifecycleDraft(register.code, draft, signal),
  });

  const unknownSuccessors = successors
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);

  return (
    <ConfirmDialog
      title={kind === 'renamed' ? 'Add a rename' : 'Mark the register closed'}
      subtitle={
        kind === 'renamed'
          ? 'A rename dated in the future is recorded exactly like one from 1989 and reads as scheduled until it takes effect.'
          : 'A closed register keeps its rules, because rights filed under it still fall due.'
      }
      confirmLabel={kind === 'renamed' ? 'Add the rename' : 'Record the closure'}
      cancelLabel="Cancel"
      pending={add.isPending}
      onCancel={onClose}
      onConfirm={() => {
        add.mutate(
          { body, etag: register.etag },
          {
            onSuccess: (written) => {
              for (const warning of written.warnings ?? []) toast.show('ok', warning);
              toast.show('ok', 'Recorded, and both chains were redrawn.');
              onClose();
            },
            onError: (error) => {
              for (const problem of problemsOf(error)) toast.show('error', problem);
            },
          },
        );
      }}
      body={
        <>
          <div className={styles.renameField} style={{ marginBottom: 12 }}>
            <Field label="Date" grow={0}>
              <DateInput value={date} min={register.earliestDate} onChange={setDate} />
            </Field>
          </div>

          {kind === 'renamed' ? (
            <div className={styles.renameField}>
              <SegmentedToggle
                aria-label="What this rename changes"
                value={field}
                onChange={setField}
                options={[
                  { value: 'name', label: 'Jurisdiction' },
                  { value: 'registry', label: 'Registry' },
                ]}
              />
              <TextInput
                value={to}
                aria-label="What the register becomes"
                placeholder="What it becomes"
                onChange={(event) => setTo(event.currentTarget.value)}
              />
            </div>
          ) : (
            <>
              <Field label="Rights passed to">
                <TextInput
                  value={successors}
                  placeholder="Separate several registers with commas"
                  onChange={(event) => setSuccessors(event.currentTarget.value)}
                />
              </Field>
              {unknownSuccessors.length > 0 ? (
                <p className={styles.warningNote}>
                  Any of {unknownSuccessors.join(', ')} the engine does not already hold will be
                  created as a register, marked incomplete, and linked — never stored as a string.
                </p>
              ) : null}
            </>
          )}

          <div style={{ marginTop: 12 }}>
            <Field label="Citation" hint="The instrument recording the event.">
              <TextInput
                value={citation}
                onChange={(event) => setCitation(event.currentTarget.value)}
              />
            </Field>
          </div>

          <ValidationMessages tone="error" messages={check.errors} pending={check.pending} />
          <ValidationMessages tone="warn" messages={check.warnings} />
        </>
      }
    />
  );
}

export function matterLabelMap(
  matters: readonly MatterSummary[],
): Readonly<Record<string, string>> {
  return Object.fromEntries(matters.map((matter) => [matter.matter, matter.label]));
}
